import { useState, useCallback, useRef, useEffect } from "react";
import axios from "axios";
import { pollChat, updateChat } from "../services/chatService";
import { removeCitationMarkers } from "../utils/textProcessing";
import { convertTextToHtml } from "../utils/textProcessing";
import { getFirstTenChars } from "../utils/chatUtils";
import { useNotification } from "../context/NotificationContext";
import { useAuth } from "../context/AuthContext";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
const USAGE_REFRESH_EVENT = "lawfusion-ai-usage-refresh";
const USAGE_REFRESH_STORAGE_KEY = "lawfusion-ai-usage-refresh-ts";
const DEFAULT_COMPARISON_QUERY =
  "Analyze the AI responses and provide a tactical comparative analysis for litigation planning. Identify where the models agree and disagree on legal strategy, risk assessment, and recommended actions. Rank critical vulnerabilities by litigation impact and recommend which position is strongest based on legal principles and factual support. Be direct and actionable—focus on what needs to be strengthened, corrected, or mitigated. Identify which AI provided the most useful analysis for this specific legal issue.";
const DEFAULT_SYNTHESIS_QUERY =
  "Produce a final legal synthesis for litigation planning using ONLY the AI responses from the Initial Query and, if available, the Comparative Analysis. Do not introduce new facts, cases, statutes, or legal theories not mentioned by the models. Analyze the issues, apply the relevant legal principles, and provide a clear, attorney-ready assessment of the client's position. Identify the primary and secondary vulnerabilities, assess whether the position is STRONG, MODERATE, or WEAK, and provide specific corrective actions to strengthen the case. Be direct and practical—focus on what will materially impact litigation outcome. Structure the response using IRAC+ format with clear section headings and lawyer-friendly language.";

/**
 * Helper function to compare file objects for changes
 * Compares files by s3Key (if available) or fileName
 */
function filesHaveChanged(currentFiles, lastSubmittedFiles) {
  const currentMain = currentFiles?.mainQueryFiles || [];
  const lastMain = lastSubmittedFiles?.mainQueryFiles || [];

  if (currentMain.length !== lastMain.length) return true;

  // Check each file
  // We assume order might matter or we sort them. Let's assume order matters for now or just check existence.
  // Simple check: check if every file in current matches one in last by s3Key/name
  return currentMain.some((curr, index) => {
    const last = lastMain[index];
    if (!last) return true;
    const currKey = curr.s3Key || curr.fileName;
    const lastKey = last.s3Key || last.fileName;
    return currKey !== lastKey;
  });
}

/** Matches WebKit/Android errors when OS suspends the tab during fetch/stream reads. */
function isLikelyBackgroundConnectionLoss(err) {
  if (!err) return false;
  const name = err.name || "";
  const msg = `${err.message || ""} ${err.cause?.message || ""}`.toLowerCase();
  const combined = `${name} ${msg}`;
  if (name === "AbortError" || combined.includes("aborted")) return false;
  return (
    combined.includes("load failed") ||
    combined.includes("failed to fetch") ||
    combined.includes("networkerror") ||
    combined.includes("network error") ||
    combined.includes("internet connection appears to be offline") ||
    combined.includes("connection was lost") ||
    combined.includes("err_connection_reset") ||
    combined.includes("econnreset") ||
    combined.includes("err_network_changed")
  );
}

/**
 * useAIQuery - Custom hook for handling AI query submission, streaming, and response management
 * @param {Function} setQuery - Function to set query state
 * @param {Function} setLastSubmittedQuery - Function to set last submitted query
 * @param {Function} setLastSubmittedFiles - Function to set last submitted files
 * @param {Function} setResponses - Function to set responses state
 * @param {Function} setSelectedModels - Function to set selected models
 * @param {Function} setCurrentChatId - Function to set current chat ID
 * @param {Function} setChats - Function to set chats array
 * @param {Function} setComparisonQuery - Function to set comparison query
 * @param {Function} setComparisonResult - Function to set comparison result
 * @param {Function} setComparisonGenerated - Function to set comparison generated flag
 * @param {Function} setSynthesisQuery - Function to set synthesis query
 * @param {Function} setSynthesisResult - Function to set synthesis result
 * @param {Function} setSynthesisGenerated - Function to set synthesis generated flag
 * @param {Function} setChatFiles - Function to set chat files
 * @param {Function} getCurrentChatFiles - Function to get current chat files
 * @param {Function} fetchAIUsage - Function to fetch AI usage stats
 * @param {Function} [getSelectedProjectId] - Optional function to get currently selected project id (for grouping chats by project)
 * @returns {Object} { queryLoading, handleSubmitQuery }
 */
export function useAIQuery({
  id,
  setQuery,
  setLastSubmittedQuery,
  setLastSubmittedFiles,
  setResponses,
  setSelectedModels,
  setCurrentChatId,
  setChats,
  setComparisonQuery,
  setComparisonResult,
  setComparisonGenerated,
  setSynthesisQuery,
  setSynthesisResult,
  setSynthesisGenerated,
  setChatFiles,
  getCurrentChatFiles,
  fetchAIUsage,
  getSelectedProjectId,
  handleAnonLimitError, // callback from Dashboard for anon limit errors
  handleTrialLimitError, // callback from Dashboard for trial limit errors
}) {
  const { showError, showWarning, showInfo } = useNotification();
  const { user, isAuthenticated } = useAuth();
  const isLoggedIn = !!isAuthenticated;
  const [queryLoading, setQueryLoading] = useState(false);
  const [queryLockedAfterStop, setQueryLockedAfterStop] = useState(false);
  const EMPTY_DOC_MESSAGE = "Attached document is empty";
  const hasAtLeastOneWord = (text) =>
    typeof text === "string" && /[\p{L}\p{N}]+/u.test(text.trim());

  // Persistence logic for stopped state
  useEffect(() => {
    if (id) {
      const isStopped = localStorage.getItem(`stopped_chat_${id}`) === "true";
      if (isStopped) {
        setQueryLockedAfterStop(true);
      } else {
        setQueryLockedAfterStop(false);
      }
    } else {
      setQueryLockedAfterStop(false);
    }
  }, [id]);
  const streamReaderRef = useRef(null);
  const streamAbortControllerRef = useRef(null);
  const lastOperationIdRef = useRef(null);
  const queryWakeLockRef = useRef(null);
  const lastStreamingChatIdRef = useRef(null);
  const streamingModelsRef = useRef([]);

  const releaseQueryWakeLock = useCallback(async () => {
    try {
      const wl = queryWakeLockRef.current;
      if (wl && typeof wl.release === "function") {
        await wl.release();
      }
    } catch (_) {
      /* already released */
    }
    queryWakeLockRef.current = null;
  }, []);

  const acquireQueryWakeLock = useCallback(async () => {
    try {
      if (!("wakeLock" in navigator)) return;
      await releaseQueryWakeLock();
      queryWakeLockRef.current = await navigator.wakeLock.request("screen");
      const wl = queryWakeLockRef.current;
      wl?.addEventListener?.("release", () => {
        queryWakeLockRef.current = null;
      });
    } catch (_) {
      /* denied or unsupported */
    }
  }, [releaseQueryWakeLock]);
  const streamingTextByModelRef = useRef({});
  const streamingBatchRef = useRef({});
  const streamingFlushTimerRef = useRef(null);
  const STREAMING_FLUSH_INTERVAL_MS = 120;
  const flushStreamingBatch = useCallback(() => {
    const pending = streamingBatchRef.current;
    const models = Object.keys(pending);
    if (models.length === 0) return;

    setResponses((prev) => {
      const updated = { ...prev };
      models.forEach((model) => {
        const payload = pending[model];
        if (!payload) return;
        updated[model] = {
          ...(updated[model] || { text: "", error: null }),
          text: payload.text,
          loading: payload.loading,
          error: payload.error ?? null,
          streamComplete: payload.streamComplete ?? false,
        };
      });
      return updated;
    });

    streamingBatchRef.current = {};
  }, [setResponses]);

  const scheduleStreamingFlush = useCallback(() => {
    if (streamingFlushTimerRef.current) return;
    streamingFlushTimerRef.current = setTimeout(() => {
      streamingFlushTimerRef.current = null;
      flushStreamingBatch();
    }, STREAMING_FLUSH_INTERVAL_MS);
  }, [flushStreamingBatch]);
  const notifyUsageRefresh = useCallback(() => {
    // Same-tab components listen to this event.
    window.dispatchEvent(new Event(USAGE_REFRESH_EVENT));

    // Cross-tab sync: storage events fire in other tabs/windows.
    try {
      localStorage.setItem(USAGE_REFRESH_STORAGE_KEY, String(Date.now()));
    } catch (e) {
      // Ignore localStorage errors (private mode / browser restrictions).
    }
  }, []);

  const handleSubmitQuery = useCallback(
    async (
      query,
      selectedModels,
      responses,
      currentChatId,
      chats,
      id,
      navigate,
      lastSubmittedQuery,
      lastSubmittedFiles,
    ) => {
      const trimmedQuery = query ? query.trim() : "";
      if (!trimmedQuery) {
        showWarning("Please enter a query", "Query Required");
        return;
      }

      // UI-level validation for random strings / special characters
      const lettersMatch = trimmedQuery.match(/\p{L}/gu);
      if (!lettersMatch || lettersMatch.length < 5) {
        showWarning("Please Enter Valid Query", "Invalid Query");
        return;
      }

      if (selectedModels.length === 0) {
        showWarning(
          "Please select at least one AI model",
          "No Models Selected",
        );
        return;
      }

      if (queryLockedAfterStop) {
        return;
      }

      // Debug: Log the selectedModels being used
      console.log(
        "[handleSubmitQuery] Selected models received:",
        selectedModels,
      );

      // Prepare attached files structure supporting arrays
      const cf = getCurrentChatFiles();
      const mainFiles = cf.mainQueryFiles || [];
      const comparisonFiles = cf.comparisonFiles || [];
      const synthesisFiles = cf.synthesisFiles || [];

      // Frontend preflight: if files are attached AND we have extracted text fields for them,
      // block immediately when the extracted text is truly empty/unreadable.
      // (Avoid false positives when fileText isn't available client-side.)
      if (mainFiles.length > 0) {
        const anyHasTextField = mainFiles.some((f) => "fileText" in (f || {}));
        if (anyHasTextField) {
          const combined = mainFiles
            .map((f) =>
              typeof f?.fileText === "string" ? f.fileText.trim() : "",
            )
            .join("\n\n");
          if (!hasAtLeastOneWord(combined)) {
            showError(EMPTY_DOC_MESSAGE);
            return;
          }
        }
      }

      const fileIds = {
        mainQueryFiles: mainFiles.map((f) => f.fileId).filter(Boolean),
        comparisonFiles: comparisonFiles.map((f) => f.fileId).filter(Boolean),
        synthesisFiles: synthesisFiles.map((f) => f.fileId).filter(Boolean),
      };

      const attachedFiles = {
        // We can send just the arrays if backend supports it.
        // Or construct mapped objects if backend anticipates multiple keys?
        // The prompt said "attachedFiles" should be an array or structured way.
        // Let's send them as arrays under keys matching the categories.
        mainQueryFiles: mainFiles.map((f) => ({
          fileId: f.fileId,
          fileName: f.fileName,
          s3Key: f.s3Key,
          s3Url: f.s3Url,
          fileType: f.fileType,
        })),
        comparisonFiles: comparisonFiles.map((f) => ({
          fileId: f.fileId,
          fileName: f.fileName,
          s3Key: f.s3Key,
          s3Url: f.s3Url,
          fileType: f.fileType,
        })),
        synthesisFiles: synthesisFiles.map((f) => ({
          fileId: f.fileId,
          fileName: f.fileName,
          s3Key: f.s3Key,
          s3Url: f.s3Url,
          fileType: f.fileType,
        })),
      };

      const queryHasChanged =
        query.trim() !== (lastSubmittedQuery || "").trim();
      const currentFiles = getCurrentChatFiles();
      const filesChanged = filesHaveChanged(currentFiles, lastSubmittedFiles);

      // When we're on a specific chat URL (/dashboard/:id):
      // 1. If files CHANGED, force a NEW chat (forking the conversation with new context)
      // 2. If files are SAME, UPDATE the existing chat (append new query)
      const urlHasExistingChatId =
        id &&
        typeof id === "string" &&
        id.trim() !== "" &&
        id !== "new" &&
        ((id.length === 16 && /^\d{16}$/.test(id)) ||
          (id.length === 24 && /^[a-f0-9]{24}$/i.test(id)));

      const currentChatHasContent = Object.values(responses || {}).some(
        (r) => r && r.text && r.text.trim() !== "",
      );

      // FORK LOGIC: If files changed, we MUST create a new chat, even if we are on an existing chat URL.
      // This preserves the old chat context with old files, and starts a new one with new files.
      // Otherwise, if not on existing chat or if current chat is empty, we also create new.
      const forceNewChat =
        filesChanged || (!urlHasExistingChatId && currentChatHasContent);

      // Query or files changed - run all selected models
      const queryOrFilesChanged = queryHasChanged || filesChanged;

      let modelsToRun;
      if (queryOrFilesChanged) {
        modelsToRun = selectedModels;
        setResponses({});
      } else {
        modelsToRun = selectedModels.filter((model) => {
          const existingResponse = responses[model];
          const hasValidResponse =
            existingResponse &&
            existingResponse.text &&
            existingResponse.text.trim() !== "" &&
            !existingResponse.error &&
            !existingResponse.loading;
          return !hasValidResponse;
        });
      }

      if (modelsToRun.length === 0) {
        showInfo(
          "All selected models already have responses. Select a new model or change your query or files to re-run.",
          "No New Models to Run",
        );
        return;
      }

      // Guest UX guard: check anon usage before toggling loading state.
      // This avoids a brief "processing" flash before the signup modal appears.
      if (!isLoggedIn) {
        try {
          const usageRes = await fetch(`${API_URL}/ai/anon-usage`, {
            credentials: "include",
          });
          if (usageRes.ok) {
            const usageData = await usageRes.json();
            if (
              usageData?.guest &&
              (usageData?.blocked || usageData?.queryExceeded || usageData?.debateExceeded)
            ) {
              if (handleAnonLimitError) {
                handleAnonLimitError(usageData);
              }
              return;
            }
          }
        } catch (e) {
          // Fail open; backend still enforces anon limits.
        }
      }

      setQueryLoading(true);

      const operationId = `op_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      lastOperationIdRef.current = operationId;

      // Fallback title for immediate UI/Chat creation
      const tempTitle = getFirstTenChars(query.trim()) || "Untitled Chat";
      // Use tempTitle as chatTitle for now
      let chatTitle = tempTitle;

      // OPTIMISTIC UI: If creating a new chat, add a temporary chat item to enable immediate highlighting
      const tempChatId = `temp_${Date.now()}`;
      let tempChatAdded = false;

      if (
        isLoggedIn &&
        (forceNewChat || (!urlHasExistingChatId && !currentChatId))
      ) {
        const tempChat = {
          _id: tempChatId,
          chatId: tempChatId,
          chatId16: tempChatId,
          title: tempTitle,
          query: query.trim(),
          userId: user?.userId || user?._id || "",
          selectedModels: selectedModels,
          responses: {},
          createdAt: new Date(),
          updatedAt: Date.now(),
          type: "research",
          isTemp: true,
        };
        setChats((prev) => [tempChat, ...prev]);
        setCurrentChatId(tempChatId);
        navigate(`/research/${tempChatId}`, { replace: true });
        tempChatAdded = true;

        // Preserve uploaded docs while we are temporarily rendering a temp chat.
        // Without this, the UI can briefly fall back to an empty file bucket
        // during the "submit -> create real chat -> navigate" transition.
        setChatFiles((prev) => {
          const newBucket = prev["new"];
          if (!newBucket) return prev;

          // Only set if temp bucket isn't already present.
          if (prev[tempChatId]) return prev;

          return {
            ...prev,
            [tempChatId]: newBucket,
          };
        });
      }

      const token = localStorage.getItem("token");

      // 1. Start Title Generation in PARALLEL (only for authenticated users)
      // Anonymous users cannot call generate-title (it requires auth)
      const titlePromise = isLoggedIn
        ? (async () => {
          try {
            const titleResponse = await fetch(
              `${API_URL}/ai/generate-title`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  ...(token && { Authorization: `Bearer ${token}` }),
                },
                credentials: "include",
                body: JSON.stringify({ query: query.trim() }),
              },
            );
            if (titleResponse.ok) {
              const titleData = await titleResponse.json();
              if (titleData.success && titleData.title) {
                console.log("[Title Generated]", titleData.title);
                return titleData.title;
              }
            }
          } catch (error) {
            console.warn("Title generation failed:", error.message);
          }
          return null;
        })()
        : Promise.resolve(null);

      // Determine chatIdToSend – use URL id when on /dashboard/:id so we UPDATE that chat.
      // Anonymous users never have a persistent chatId – always null.
      let chatIdToSend = null;
      if (!isLoggedIn) {
        // Anon: in-memory only, never persist
        chatIdToSend = null;
      } else if (forceNewChat) {
        chatIdToSend = null;
      } else if (urlHasExistingChatId) {
        // We're on /dashboard/:id – always use URL id so we UPDATE this chat, never create.
        chatIdToSend = id;
      } else if (currentChatId) {
        chatIdToSend = currentChatId;
      } else if (id && id !== "new") {
        const chatFromUrl = chats.find((c) => {
          const chatId16 = c.chatId16 || c.chatId || c._id;
          return (
            chatId16 === id ||
            c.chatId === id ||
            c._id === id ||
            String(c.chatId16) === String(id)
          );
        });
        if (chatFromUrl) {
          chatIdToSend = chatFromUrl.chatId || chatFromUrl._id;
        } else {
          const isLikelyExistingChatId =
            typeof id === "string" &&
            id.trim() !== "" &&
            ((id.length === 16 && /^\d{16}$/.test(id)) || id.length === 24);
          if (isLikelyExistingChatId) {
            chatIdToSend = id;
          }
        }
      }
      chatIdToSend = chatIdToSend || null;

      // Create real chat via API if needed (only for authenticated users)
      if (isLoggedIn && (!chatIdToSend || chatIdToSend === "new")) {
        try {
          // Create real chat via API first - DO NOT send chatId, let backend generate real 16-digit ID
          // Debug: Log what we're sending
          console.log(
            "[handleSubmitQuery] Creating chat with selectedModels:",
            selectedModels,
          );
          // Get currently selected project (if any) so new chat is grouped correctly
          const currentProjectId =
            typeof getSelectedProjectId === "function"
              ? getSelectedProjectId()
              : null;
          const chatData = {
            title: chatTitle,
            query: query.trim(),
            selectedModels: selectedModels, // Use the selectedModels parameter passed to this function
            responses: {},
            type: "research",
            chatType: "research",
            ...(currentProjectId
              ? {
                projectId: currentProjectId,
              }
              : {}),
            // Explicitly NOT sending chatId - backend will generate a real 16-digit ID
            fileIds: fileIds, // New file link map for DB counters
            attachedFiles: attachedFiles, // Save attached files to DB
          };

          console.log("[useAIQuery] Creating new chat with data:", chatData);
          console.log("[useAIQuery] Attached files payload:", attachedFiles);

          const createChatResponse = await axios.post(
            `${API_URL}/ai/chats`,
            chatData,
            {
              headers: token ? { Authorization: `Bearer ${token}` } : {},
              withCredentials: true,
            },
          );

          const newChat = createChatResponse.data.chat;
          // Debug: Log what backend returned
          console.log(
            "[handleSubmitQuery] Backend returned chat with selectedModels:",
            newChat.selectedModels,
          );

          // Use MongoDB _id as the primary identifier (real ID)
          const realChatId = newChat._id; // MongoDB ObjectId like "69734e866f78af0003b44754"
          // Use the real 16-digit chatId from backend (not temp)
          const realChatId16 = newChat.chatId; // Real 16-digit ID like "0942235711623462"

          // LOG: Chat created with all IDs
          console.log(
            "[CHAT CREATED] ============================================",
          );
          console.log("[CHAT CREATED] Timestamp:", new Date().toISOString());
          console.log("[CHAT CREATED] MongoDB _id:", realChatId);
          console.log("[CHAT CREATED] 16-digit chatId:", realChatId16);
          console.log(
            "[CHAT CREATED] chatId16 (alias):",
            newChat.chatId16 || realChatId16,
          );
          console.log("[CHAT CREATED] userId:", newChat.userId);
          console.log("[CHAT CREATED] title:", newChat.title);
          console.log("[CHAT CREATED] query:", newChat.query);
          console.log("[CHAT CREATED] selectedModels:", newChat.selectedModels);
          console.log(
            "[CHAT CREATED] ============================================",
          );

          // Ensure we use the generated title, not "New Chat" default
          const finalTitle =
            chatTitle &&
              chatTitle !== "New Chat" &&
              chatTitle !== "Generating title..."
              ? chatTitle
              : newChat.title && newChat.title !== "New Chat"
                ? newChat.title
                : chatTitle;

          // Use the selectedModels from backend response, or fallback to what was sent
          // Backend should return the exact selectedModels that were saved
          const chatSelectedModels =
            newChat.selectedModels &&
              Array.isArray(newChat.selectedModels) &&
              newChat.selectedModels.length > 0
              ? newChat.selectedModels
              : selectedModels; // Fallback to what we sent if backend doesn't return it

          // Debug: Log what we're using for UI
          console.log(
            "[handleSubmitQuery] Using selectedModels for UI:",
            chatSelectedModels,
          );

          // Prepare chat object for sidebar with real IDs
          const chatForSidebar = {
            _id: realChatId, // MongoDB ObjectId - the real ID
            chatId: realChatId16, // Real 16-digit chatId from backend
            chatId16: realChatId16, // For URL matching compatibility
            title: finalTitle, // Use generated title, not "New Chat"
            query: newChat.query || query.trim(),
            userId: newChat.userId || user?.userId || user?._id || "",
            selectedModels: chatSelectedModels, // Use backend's selectedModels or what we sent
            responses: newChat.responses || {},
            // Ensure new chat appears under correct project in sidebar
            projectId:
              newChat.projectId ||
              (typeof getSelectedProjectId === "function"
                ? getSelectedProjectId()
                : null) ||
              null,
            createdAt: newChat.createdAt
              ? new Date(newChat.createdAt)
              : new Date(),
            updatedAt: newChat.updatedAt || Date.now(),
            type: newChat.type || "research",
          };

          // Add real chat to sidebar immediately with real IDs and remove temp optimistic chat
          setChats((prev) => {
            const filtered = prev.filter(
              (c) => c._id !== tempChatId && c.chatId !== tempChatId,
            );
            return [chatForSidebar, ...filtered];
          });

          // Preserve files when creating chat - copy from 'new' / temp bucket to real chat ID.
          // IMPORTANT: Do not rely on a delayed clear of chatFiles["new"] — createChat is async and a
          // short timeout can delete "new" before this runs, leaving real buckets empty until refresh.
          setChatFiles((prev) => {
            const bucketLen = (b) => {
              if (!b) return 0;
              const m = b.mainQueryFiles || b.mainQueryFile;
              const c = b.comparisonFiles || b.comparisonFile;
              const s = b.synthesisFiles || b.synthesisFile;
              const n = (x) =>
                Array.isArray(x) ? x.length : x && typeof x === "object" ? 1 : 0;
              return n(m) + n(c) + n(s);
            };

            const newChatFiles = prev["new"] || {};
            const tempBucket =
              tempChatAdded && tempChatId ? prev[tempChatId] || {} : {};
            const currentChatFiles = prev[currentChatId] || {};

            let sourceFiles = newChatFiles;
            if (bucketLen(sourceFiles) === 0 && bucketLen(tempBucket) > 0) {
              sourceFiles = tempBucket;
            }
            if (
              forceNewChat &&
              currentChatId &&
              bucketLen(currentChatFiles) > 0
            ) {
              sourceFiles = currentChatFiles;
            }

            const filesToPreserve = {
              mainQueryFiles:
                sourceFiles.mainQueryFiles ||
                sourceFiles.mainQueryFile ||
                [],
              comparisonFiles:
                sourceFiles.comparisonFiles ||
                sourceFiles.comparisonFile ||
                [],
              synthesisFiles:
                sourceFiles.synthesisFiles ||
                sourceFiles.synthesisFile ||
                [],
            };

            const realKey16 =
              realChatId16 !== undefined && realChatId16 !== null
                ? String(realChatId16)
                : realChatId16;
            const next = {
              ...prev,
              [realChatId]: filesToPreserve,
              [realKey16]: filesToPreserve,
            };
            delete next["new"];
            if (tempChatAdded && tempChatId) delete next[tempChatId];
            return next;
          });

          // Ensure queryLoading is true for the new state
          setQueryLoading(true);

          // Preserve the query - don't let it get reset
          setQuery(query.trim());
          setLastSubmittedQuery(query.trim());

          // CRITICAL: Set selectedModels from the chat object (backend response), not from current state
          // This ensures UI shows the correct models that were saved to the database
          // Do this BEFORE setting currentChatId to ensure state is updated
          console.log(
            "[handleSubmitQuery] Setting selectedModels to:",
            chatSelectedModels,
          );
          setSelectedModels(chatSelectedModels);

          // Use 16-digit chatId for currentChatId to match URL and enable proper chat loading
          // This ensures Dashboard component can match currentChatId with the URL id
          console.log(
            "[handleSubmitQuery] Setting currentChatId to:",
            realChatId16,
            "for chat with _id:",
            realChatId,
            "chatId:",
            realChatId16,
          );
          setCurrentChatId(realChatId16); // Use 16-digit chatId for consistency with URL
          chatIdToSend = realChatId16; // Use 16-digit for stream/update so backend finds same chat by chatId

          // Small delay to ensure state updates propagate before navigation
          // This prevents the useEffect from running before state is updated
          await new Promise((resolve) => setTimeout(resolve, 100));

          // Navigate using the real 16-digit chatId (not temp, not _id)
          navigate(`/research/${realChatId16}`, { replace: true });

          // After navigation, ensure query and selectedModels are still set correctly
          // This is a safeguard in case useEffect tries to reset them
          setTimeout(() => {
            setQuery(query.trim()); // Re-set query to ensure it's visible
            setSelectedModels(chatSelectedModels);
            console.log(
              "[handleSubmitQuery] Safeguard: Re-setting query and selectedModels after navigation:",
              {
                query: query.trim(),
                selectedModels: chatSelectedModels,
              },
            );
          }, 200);
        } catch (error) {
          console.error("Error creating chat:", error);
          if (error.response?.status === 403) {
            const errType =
              error.response?.data?.error ||
              error.response?.data?.message ||
              "";
            if (errType.includes("NO_SUBSCRIPTION")) {
              showWarning(
                "You must have an active subscription or trial to use files.",
                "Subscription Required",
              );
            } else if (errType.includes("PER_CHAT_FILE_LIMIT")) {
              showWarning(
                "You have attached too many files for one chat.",
                "Limit Exceeded",
              );
            } else if (errType.includes("TRIAL_UPLOAD_LIMIT_REACHED")) {
              showWarning(
                "Committing these files would exceed your 10 file trial upload limit.",
                "Trial Limit Reached",
              );
            } else {
              showError(
                error.response.data.message ||
                "Permission denied when creating chat.",
                "Permission Error",
              );
            }
          } else {
            const msg = error.response?.data?.message;
            showError(
              msg || "Failed to create chat. Please try again.",
              "Chat Creation Error",
            );
          }
          setQueryLoading(false);
          if (tempChatAdded) {
            setChats((prev) =>
              prev.filter(
                (c) => c._id !== tempChatId && c.chatId !== tempChatId,
              ),
            );
            setCurrentChatId(id !== "new" && id !== "temp" ? id : null);
            if (id && id !== "new" && id !== "temp") {
              navigate(`/research/${id}`, { replace: true });
            } else {
              navigate("/research", { replace: true });
            }
          }
          setQueryLoading(false);
          return; // Don't proceed if chat creation fails
        }
      }

      // Now update title if/when it generates, in background
      if (chatIdToSend) {
        // If files changed in an existing chat, update them
        if (filesChanged && !forceNewChat) {
          updateChat(chatIdToSend, { attachedFiles }).catch((err) => {
            console.error("Failed to update attached files:", err);
          });
        }

        titlePromise.then(async (generatedTitle) => {
          if (generatedTitle && generatedTitle !== tempTitle) {
            try {
              await updateChat(chatIdToSend, { title: generatedTitle });
              // Update UI state with new title
              setChats((prev) =>
                prev.map((c) =>
                  c._id === chatIdToSend || c.chatId === chatIdToSend
                    ? { ...c, title: generatedTitle }
                    : c,
                ),
              );
            } catch (err) {
              console.warn(
                "Failed to update chat title with generated one",
                err,
              );
            }
          }
        });
      }

      // Query loading is already set at the start of handleSubmitQuery
      setComparisonQuery(DEFAULT_COMPARISON_QUERY);
      setComparisonResult("");
      setComparisonGenerated(false);
      setSynthesisQuery(DEFAULT_SYNTHESIS_QUERY);
      setSynthesisResult("");
      setSynthesisGenerated(false);

      let updatedResponses = {};
      selectedModels.forEach((model) => {
        if (responses[model]) {
          const existingResponse = responses[model];
          const isModelToRun = modelsToRun.includes(model);
          if (isModelToRun) {
            updatedResponses[model] = { text: "", loading: true, error: null };
          } else {
            updatedResponses[model] = { ...existingResponse };
          }
        } else if (modelsToRun.includes(model)) {
          updatedResponses[model] = { text: "", loading: true, error: null };
        }
      });
      // Preserve responses for models that were deselected (not in selectedModels)
      // so re-selecting them later still shows their previous response in the same chat.
      Object.keys(responses || {}).forEach((model) => {
        if (!selectedModels.includes(model) && responses[model]) {
          updatedResponses[model] = { ...responses[model] };
        }
      });
      setResponses(updatedResponses);

      try {
        streamingBatchRef.current = {};
        streamingTextByModelRef.current = {};
        if (streamingFlushTimerRef.current) {
          clearTimeout(streamingFlushTimerRef.current);
          streamingFlushTimerRef.current = null;
        }

        let authToken = token;
        if (!authToken) {
          try {
            const tokenResponse = await axios.get(`${API_URL}/auth/token`);
            authToken = tokenResponse.data.token;
            if (authToken) {
              localStorage.setItem("token", authToken);
            }
          } catch (tokenError) {
            console.warn("Could not get token from backend:", tokenError);
          }
        }

        const headers = {
          "Content-Type": "application/json",
        };
        if (authToken) {
          headers["Authorization"] = `Bearer ${authToken}`;
        }

        // attachedFiles is already prepared above

        // Also legacy fields for backward compat if needed?
        // Or just `fileText` from first file?
        // Note: `fileText` param in body is likely used for direct context injection.
        // If multiple files, which text? Maybe concat?
        // Let's assume backend handles `attachedFiles`.
        // But let's concat text for `fileText` just in case backend relies on it for context.
        // Only send real extracted text as `fileText`.
        // Avoid adding wrapper headers because they can make truly-empty documents look non-empty.
        const combinedFileText = mainFiles
          .map((f) =>
            typeof f?.fileText === "string" ? f.fileText.trim() : "",
          )
          .filter((t) => /[\p{L}\p{N}]+/u.test(t))
          .join("\n\n");

        const streamAbortController = new AbortController();
        streamAbortControllerRef.current = streamAbortController;

        lastStreamingChatIdRef.current =
          chatIdToSend ||
          (typeof id === "string" &&
          id !== "new" &&
          !String(id).startsWith("temp_")
            ? id
            : null);
        streamingModelsRef.current = [...modelsToRun];

        await acquireQueryWakeLock();

        const currentProjectId =
          typeof getSelectedProjectId === "function"
            ? getSelectedProjectId()
            : null;

        const response = await fetch(`${API_URL}/ai/query-stream`, {
          method: "POST",
          headers: headers,
          credentials: "include",
          signal: streamAbortController.signal,
          body: JSON.stringify({
            prompt: query,
            title: chatTitle,
            models: modelsToRun,
            selectedModels: selectedModels,
            chatId: chatIdToSend,
            operationId: operationId,
            fileText: combinedFileText || null,
            fileName:
              mainFiles.length > 0
                ? mainFiles.map((f) => f.fileName).join(", ")
                : null, // Support multiple file names for context reference
            attachedFiles: attachedFiles,
            projectId: currentProjectId || null,
          }),
        });

        if (!response.ok) {
          if (response.status === 401) {
            throw new Error("STREAM_AUTH_FAILED");
          }

          if (response.status === 429 || response.status === 403) {
            try {
              const errData = await response.json();
              if (errData.anonLimitExceeded) {
                // Reset all model loading spinners
                setResponses((prev) => {
                  const updated = { ...prev };
                  Object.keys(updated).forEach((model) => {
                    if (updated[model]?.loading) {
                      updated[model] = {
                        ...updated[model],
                        loading: false,
                        error: null,
                      };
                    }
                  });
                  return updated;
                });
                setQueryLoading(false);

                // FUTURE USE: toast-only feedback for free-limit blocks.
                // Disabled to ensure only the modal dialog is shown (matches Debate Mode UX).
                // showWarning(
                //   errData.message ||
                //   "You've used all your free demo queries. Sign up to continue.",
                //   "Free Limit Reached",
                // );

                if (handleAnonLimitError) {
                  handleAnonLimitError(errData);
                }
                return;
              }

              if (errData.limitType && errData.limitType.startsWith("TRIAL_")) {
                // Reset all model loading spinners
                setResponses((prev) => {
                  const updated = { ...prev };
                  Object.keys(updated).forEach((model) => {
                    if (updated[model]?.loading) {
                      updated[model] = {
                        ...updated[model],
                        loading: false,
                        error: null,
                      };
                    }
                  });
                  return updated;
                });
                setQueryLoading(false);

                // Trigger the trial limit handler
                if (handleTrialLimitError) {
                  handleTrialLimitError(errData);
                } else {
                  showWarning(
                    errData.message || "Free trial limit reached.",
                    "Trial Limit",
                  );
                }
                return;
              }
            } catch (e) {
              // ignore parse error — fall through to generic error handling
            }
          }

          if (response.status === 400) {
            let errorMessage =
              "This request is outside the scope of legal analysis. Please rephrase it as a legal issue or provide a legal document for review.";
            let isValidationError = false;
            try {
              const errorData = await response.json();
              if (errorData.validationError) {
                isValidationError = true;
                // Preserve requested UX copy for empty-document blocks.
                if (errorData.emptyDocument) {
                  errorMessage = EMPTY_DOC_MESSAGE;
                } else {
                  errorMessage = errorData.message || errorMessage;
                }
              }
            } catch (e) {
              isValidationError = true;
            }

            if (isValidationError) {
              setResponses((prev) => {
                const updated = {};
                selectedModels.forEach((model) => {
                  updated[model] = {
                    text: "",
                    loading: false,
                    error: errorMessage,
                  };
                });
                return updated;
              });
              setQueryLoading(false);
              showError(
                errorMessage,
                errorMessage === EMPTY_DOC_MESSAGE ? "Empty Document" : "Out of Scope",
              );
              return;
            }
          }

          let errorMessage = "Error processing query";
          try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorMessage;
          } catch (e) {
            errorMessage = `Server error: ${response.status} ${response.statusText}`;
          }
          throw new Error(errorMessage);
        }

        if (!response.body || typeof response.body.getReader !== "function") {
          const resumeChatId = isLoggedIn
            ? chatIdToSend || currentChatId || (id && id !== "new" ? id : null)
            : null;

          if (resumeChatId) {
            for (let attempt = 0; attempt < 45; attempt++) {
              try {
                const chat = await pollChat(resumeChatId);
                const hasAllResponses = modelsToRun.every((model) =>
                  (chat?.responses?.[model] || "").trim(),
                );

                if (chat?.responses) {
                  setResponses((prev) => {
                    const next = { ...prev };
                    modelsToRun.forEach((model) => {
                      const text = chat.responses?.[model] || "";
                      if (!text.trim()) return;
                      next[model] = {
                        ...(next[model] || {}),
                        text: removeCitationMarkers(text),
                        loading: !hasAllResponses,
                        error: null,
                        streamComplete: hasAllResponses,
                      };
                    });
                    return next;
                  });
                }

                if (hasAllResponses) {
                  setQueryLoading(false);
                  return;
                }
              } catch (_) {
                // Continue retrying; mobile background resume can be transient.
              }
              await new Promise((resolve) => setTimeout(resolve, 2000));
            }
          }

          throw new Error(
            "Connection was interrupted while the app was in the background. Please try your query again.",
          );
        }
        const reader = response.body.getReader();
        streamReaderRef.current = reader;
        const decoder = new TextDecoder();
        let buffer = "";
        let currentEvent = null;
        let finalData = null;
        let streamStartTime = Date.now();
        const STREAM_TIMEOUT = 180000;
        const completedModels = new Set();
        const expectedModels = new Set(modelsToRun);
        let streamError = null;
        let streamAborted = false;

        const pollForResponses = async (chatId, maxAttempts = 60) => {
          if (!chatId) {
            setQueryLoading(false);
            return false;
          }

          const lastResponseLengths = {};

          for (let attempt = 0; attempt < maxAttempts; attempt++) {
            if (streamAborted) {
              setQueryLoading(false);
              break;
            }

            try {
              const chat = await pollChat(chatId);
              if (chat && chat.responses) {
                const missingModels = modelsToRun.filter((model) => {
                  const hasResponse =
                    chat.responses[model] && chat.responses[model].trim();
                  const alreadyCompleted = completedModels.has(model);
                  return !alreadyCompleted && hasResponse;
                });

                if (missingModels.length > 0) {
                  missingModels.forEach((model) => {
                    const responseText = chat.responses[model];
                    if (responseText && responseText.trim()) {
                      const currentLength = responseText.length;
                      const lastLength = lastResponseLengths[model] || 0;

                      setResponses((prev) => {
                        const updated = { ...prev };
                        updated[model] = {
                          text: removeCitationMarkers(responseText),
                          formatted: convertTextToHtml(
                            removeCitationMarkers(responseText),
                          ),
                          loading: currentLength > lastLength,
                          error: null,
                          streamComplete:
                            currentLength === lastLength && lastLength > 0,
                        };
                        return updated;
                      });

                      if (currentLength === lastLength && lastLength > 0) {
                        completedModels.add(model);
                      }

                      lastResponseLengths[model] = currentLength;
                    }
                  });
                }

                const allComplete = modelsToRun.every((model) => {
                  const isCompleted = completedModels.has(model);
                  const hasStableResponse =
                    chat.responses &&
                    chat.responses[model] &&
                    chat.responses[model].trim() &&
                    lastResponseLengths[model] &&
                    lastResponseLengths[model] === chat.responses[model].length;
                  return isCompleted || hasStableResponse;
                });

                if (allComplete) {
                  modelsToRun.forEach((model) => {
                    if (chat.responses[model] && chat.responses[model].trim()) {
                      setResponses((prev) => {
                        const updated = { ...prev };
                        if (!updated[model] || !updated[model].streamComplete) {
                          updated[model] = {
                            text: removeCitationMarkers(chat.responses[model]),
                            formatted: convertTextToHtml(
                              removeCitationMarkers(chat.responses[model]),
                            ),
                            loading: false,
                            error: null,
                            streamComplete: true,
                          };
                        }
                        return updated;
                      });
                      completedModels.add(model);
                    }
                  });
                  setQueryLoading(false);
                  return true;
                }
              }
            } catch (pollError) {
              console.warn("Polling error:", pollError);
            }

            await new Promise((resolve) => setTimeout(resolve, 2000));
          }

          modelsToRun.forEach((model) => {
            if (!completedModels.has(model)) {
              console.warn(
                `Model ${model} did not complete within polling timeout`,
              );
              completedModels.add(model);
            }
          });

          setQueryLoading(false);
          return false;
        };

        const timeoutId = setTimeout(() => {
          if (!streamAborted) {
            console.warn("Stream timeout, switching to polling");
            streamAborted = true;
            reader.cancel().catch(() => { });

            const effectiveChatId = currentChatId || chatIdToSend;
            if (effectiveChatId) {
              pollForResponses(effectiveChatId).then(() => {
                setQueryLoading(false);
              });
            } else {
              setQueryLoading(false);
            }
          }
        }, STREAM_TIMEOUT);

        try {
          while (true) {
            if (streamAborted) break;

            if (Date.now() - streamStartTime > STREAM_TIMEOUT) {
              streamAborted = true;
              break;
            }

            const { done, value } = await reader.read();
            if (done) {
              clearTimeout(timeoutId);
              break;
            }

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split("\n");
            buffer = lines.pop() || "";

            for (const line of lines) {
              if (line.startsWith("event: ")) {
                currentEvent = line.slice(7).trim();
              } else if (line.startsWith("data: ")) {
                try {
                  const data = JSON.parse(line.slice(6));

                  if (currentEvent === "chunk") {
                    const model = data.model;
                    const currentText = streamingTextByModelRef.current[model] || "";
                    const nextText =
                      typeof data.fullText === "string"
                        ? data.fullText
                        : `${currentText}${data.text || ""}`;
                    streamingTextByModelRef.current[model] = nextText;

                    streamingBatchRef.current[data.model] = {
                      text: removeCitationMarkers(nextText),
                      loading: true,
                      error: null,
                      streamComplete: false,
                    };
                    scheduleStreamingFlush();
                  } else if (currentEvent === "model-start") {
                    setResponses((prev) => {
                      const updated = { ...prev };
                      updated[data.model] = {
                        text: "",
                        loading: true,
                        error: null,
                        streamComplete: false,
                      };
                      return updated;
                    });
                  } else if (currentEvent === "model-done") {
                    if (streamingFlushTimerRef.current) {
                      clearTimeout(streamingFlushTimerRef.current);
                      streamingFlushTimerRef.current = null;
                    }
                    flushStreamingBatch();
                    completedModels.add(data.model);
                    streamingTextByModelRef.current[data.model] = data.text || "";
                    setResponses((prev) => {
                      const updated = { ...prev };
                      updated[data.model] = {
                        text: removeCitationMarkers(data.text),
                        loading: false,
                        error: null,
                        streamComplete: true,
                      };
                      return updated;
                    });
                  } else if (currentEvent === "model-error") {
                    if (streamingFlushTimerRef.current) {
                      clearTimeout(streamingFlushTimerRef.current);
                      streamingFlushTimerRef.current = null;
                    }
                    flushStreamingBatch();
                    completedModels.add(data.model);
                    setResponses((prev) => {
                      const updated = { ...prev };
                      updated[data.model] = {
                        text: "",
                        loading: false,
                        error: data.error,
                      };
                      return updated;
                    });
                  } else if (currentEvent === "complete") {
                    clearTimeout(timeoutId);
                    finalData = data;
                  } else if (currentEvent === "error") {
                    if (data.validationError) {
                      const validationMessage =
                        data.message ||
                        "This request is outside the scope of legal analysis. Please rephrase it as a legal issue or provide a legal document for review.";
                      showError(validationMessage, "Out of Scope");

                      setResponses((prev) => {
                        const updated = {};
                        selectedModels.forEach((model) => {
                          updated[model] = {
                            text: "",
                            loading: false,
                            error: validationMessage,
                          };
                        });
                        return updated;
                      });

                      clearTimeout(timeoutId);
                      setQueryLoading(false);
                      streamAborted = true;
                      return;
                    }
                    streamError = new Error(
                      data.message || "Error processing query",
                    );
                    streamAborted = true;
                    break;
                  }
                } catch (e) {
                  console.error("Error parsing SSE data:", e, line);
                }
                currentEvent = null;
              } else if (line.trim() === "") {
                currentEvent = null;
              }
            }
          }
        } catch (readError) {
          if (readError?.name === "AbortError") {
            streamAborted = true;
            setQueryLoading(false);
            setQueryLockedAfterStop(true);
            // Persist stopped state for this chat so UI shows on refresh
            try {
              if (id) {
                localStorage.setItem(`stopped_chat_${id}`, "true");
              }
            } catch (e) {
              // ignore storage errors
            }
            streamReaderRef.current = null;
          } else {
            console.error("Stream read error:", readError);
            streamError = readError;
          }
        } finally {
          clearTimeout(timeoutId);
          if (streamingFlushTimerRef.current) {
            clearTimeout(streamingFlushTimerRef.current);
            streamingFlushTimerRef.current = null;
          }
          flushStreamingBatch();
          streamReaderRef.current = null;
          streamAbortControllerRef.current = null;

          const effectiveChatId =
            finalData?.chatId || currentChatId || chatIdToSend;

          if (streamError && !streamAborted) {
            const connLoss = isLikelyBackgroundConnectionLoss(streamError);
            if (connLoss && isLoggedIn && effectiveChatId) {
              await pollForResponses(effectiveChatId);
            }

            const allModelsDoneNow = modelsToRun.every((model) =>
              completedModels.has(model),
            );

            if (!allModelsDoneNow) {
              setQueryLoading(false);
              if (connLoss) {
                showWarning(
                  "Your connection paused while LawFusion was in the background. Anything the server already saved still appears below. Run the query again if a response stopped mid-stream.",
                  "Connection paused",
                );
              } else {
                throw streamError;
              }
            }
          }

          const missingModels = modelsToRun.filter(
            (model) => !completedModels.has(model),
          );

          if (missingModels.length > 0 && effectiveChatId && !streamAborted) {
            console.log(
              `Polling for missing models: ${missingModels.join(", ")}`,
            );
            const allComplete = await pollForResponses(effectiveChatId);
            if (!allComplete) {
              console.warn(
                "Some models did not complete after polling:",
                missingModels,
              );
              setQueryLoading(false);
            } else {
              console.log("All models completed via polling");
              setQueryLoading(false);
            }
          } else if (!finalData) {
            setQueryLoading(false);
          } else if (finalData && missingModels.length === 0) {
            setQueryLoading(false);
          }
        }

        const allModelsComplete = modelsToRun.every((model) =>
          completedModels.has(model),
        );

        if (finalData) {
          // For anonymous users, chatId will be null — don't try to navigate or poll
          if (!allModelsComplete && finalData.chatId && isLoggedIn) {
            const effectiveChatId =
              finalData.chatId || currentChatId || chatIdToSend;
            if (effectiveChatId) {
              await pollForResponses(effectiveChatId, 30);
              setQueryLoading(false);
            } else {
              setQueryLoading(false);
            }
          } else if (allModelsComplete) {
            setQueryLoading(false);
          } else {
            setQueryLoading(false);
          }
          setLastSubmittedQuery(query.trim());
          // Save current files state for change detection
          const filesToSave = getCurrentChatFiles();
          setLastSubmittedFiles(
            filesToSave
              ? {
                mainQueryFiles: (filesToSave.mainQueryFiles || []).map(
                  (f) => ({
                    s3Key: f.s3Key,
                    fileName: f.fileName,
                  }),
                ),
              }
              : null,
          );

          // Track anon usage in localStorage for the usage counter UI
          if (!isLoggedIn) {
            try {
              const used = parseInt(
                localStorage.getItem("anon_uses") || "0",
                10,
              );
              localStorage.setItem("anon_uses", String(used + 1));
            } catch (e) {
              // ignore storage errors
            }
          }

          // Only run post-query server sync for authenticated users (anon has no chatId)
          if (isLoggedIn && finalData.chatId) {
            const effectiveChatId = finalData.chatId || currentChatId;
            const tempChatIdToRemove =
              currentChatId && currentChatId.startsWith("temp_")
                ? currentChatId
                : null;

            setCurrentChatId((prev) => {
              if (finalData.chatId && prev !== finalData.chatId) {
                return finalData.chatId;
              }
              return prev;
            });

            if (effectiveChatId && effectiveChatId !== "new") {
              try {
                await new Promise((resolve) => setTimeout(resolve, 500));

                const token = localStorage.getItem("token");
                const latestChatResponse = await axios.get(
                  `${API_URL}/ai/chats/${encodeURIComponent(effectiveChatId)}`,
                  {
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                    withCredentials: true,
                  },
                );

                const latestChat = latestChatResponse.data.chat;
                const responsesToSave = {};

                modelsToRun.forEach((model) => {
                  if (latestChat.responses && latestChat.responses[model]) {
                    responsesToSave[model] = latestChat.responses[model];
                  }
                });

                if (Object.keys(responsesToSave).length > 0) {
                  setResponses((prev) => {
                    const updated = { ...prev };
                    Object.keys(responsesToSave).forEach((model) => {
                      updated[model] = {
                        text: removeCitationMarkers(responsesToSave[model]),
                        formatted: convertTextToHtml(
                          removeCitationMarkers(responsesToSave[model]),
                        ),
                        loading: false,
                        error: null,
                        streamComplete: true,
                      };
                    });
                    return updated;
                  });
                }

                if (tempChatIdToRemove) {
                  setChats((prev) =>
                    prev.filter(
                      (c) =>
                        c._id !== tempChatIdToRemove &&
                        c.chatId !== tempChatIdToRemove,
                    ),
                  );
                }

                await fetchAIUsage();
                notifyUsageRefresh();
              } catch (error) {
                console.error("Error fetching latest chat:", error);
              }
            }
          }
        }
      } catch (error) {
        streamAbortControllerRef.current = null;
        setQueryLoading(false);

        if (error.name === "AbortError") {
          return;
        }
        console.error("Query submission error:", error);
        if (error.message === "STREAM_AUTH_FAILED") {
          showError(
            "Authentication failed. Please try logging in again.",
            "Authentication Error",
          );
        } else if (isLikelyBackgroundConnectionLoss(error)) {
          showWarning(
            "The network connection paused while LawFusion was in the background. If results look incomplete, run your query again once you are back online.",
            "Connection paused",
          );
        } else {
          showError(
            error.message || "Error processing query. Please try again.",
            "Query Error",
          );
        }
      } finally {
        await releaseQueryWakeLock();
        lastStreamingChatIdRef.current = null;
        streamingModelsRef.current = [];
      }
    },
    [
      user,
      isAuthenticated,
      showError,
      showWarning,
      showInfo,
      setQuery,
      setLastSubmittedQuery,
      setLastSubmittedFiles,
      setResponses,
      setSelectedModels,
      setCurrentChatId,
      setChats,
      setComparisonQuery,
      setComparisonResult,
      setComparisonGenerated,
      setSynthesisQuery,
      setSynthesisResult,
      setSynthesisGenerated,
      setChatFiles,
      getCurrentChatFiles,
      fetchAIUsage,
      notifyUsageRefresh,
      flushStreamingBatch,
      scheduleStreamingFlush,
      queryLockedAfterStop,
      handleAnonLimitError,
      getSelectedProjectId,
      acquireQueryWakeLock,
      releaseQueryWakeLock,
    ],
  );

  useEffect(() => {
    if (!isLoggedIn || !queryLoading) return;

    let cancelled = false;
    let timer = null;

    const readStreamedChat = async () => {
      if (cancelled) return;
      if (document.visibilityState !== "visible") return;

      const chatIdRaw = lastStreamingChatIdRef.current;
      const chatId =
        chatIdRaw ||
        (typeof id === "string" &&
        id !== "new" &&
        !String(id).startsWith("temp_")
          ? id
          : null);
      if (!chatId || String(chatId).startsWith("temp_")) return;

      const models = streamingModelsRef.current;
      if (!models?.length) return;

      try {
        const chat = await pollChat(chatId);
        if (!chat?.responses) return;
        setResponses((prev) => {
          const next = { ...prev };
          let changed = false;
          for (const model of models) {
            const raw = chat.responses[model];
            const text =
              typeof raw === "string"
                ? raw
                : raw && (raw.text ?? raw.content)
                  ? String(raw.text ?? raw.content)
                  : "";
            if (!text.trim()) continue;
            const cleaned = removeCitationMarkers(text);
            if (prev[model]?.text !== cleaned || prev[model]?.loading) {
              next[model] = {
                ...(next[model] || {}),
                text: cleaned,
                formatted: convertTextToHtml(cleaned),
                loading: true,
                error: null,
                streamComplete: false,
              };
              changed = true;
            }
          }
          return changed ? next : prev;
        });
      } catch (_) {
        /* network may still be waking after app switch */
      }
    };

    const onVisibility = () => {
      if (document.visibilityState !== "visible") return;
      void acquireQueryWakeLock();
      void readStreamedChat();
    };

    void readStreamedChat();
    timer = window.setInterval(() => {
      void readStreamedChat();
    }, 1800);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      cancelled = true;
      if (timer) {
        window.clearInterval(timer);
      }
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [queryLoading, isLoggedIn, id, setResponses, acquireQueryWakeLock]);

  const stopInitialQuery = useCallback(() => {
    const opId = lastOperationIdRef.current;

    // 1. Abort the fetch so the server sees connection close (works behind proxies when client aborts)
    if (streamAbortControllerRef.current) {
      try {
        streamAbortControllerRef.current.abort();
      } catch (e) {
        // ignore if already aborted
      }
      streamAbortControllerRef.current = null;
    }
    if (streamReaderRef.current) {
      streamReaderRef.current.cancel().catch(() => { });
      streamReaderRef.current = null;
    }

    // 1.5 Update responses - set loading: false for all models so they stop "thinking"
    setResponses((prev) => {
      const updated = { ...prev };
      Object.keys(updated).forEach((model) => {
        if (updated[model] && updated[model].loading) {
          updated[model] = {
            ...updated[model],
            loading: false,
            // If text is empty or only whitespace, show a stopped message
            text:
              updated[model].text && updated[model].text.trim()
                ? updated[model].text
                : "The query is stopped.",
          };
        }
      });
      return updated;
    });

    setQueryLoading(false);
    setQueryLockedAfterStop(true);
    // Persist stopped state for this chat so UI shows on refresh
    try {
      if (id) {
        localStorage.setItem(`stopped_chat_${id}`, "true");
      }
    } catch (e) {
      // ignore storage errors
    }

    if (!opId) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const headers = {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      // 2. Tell backend to stop this operationId (handles same-instance and delayed connection close)
      fetch(`${API_URL}/ai/query-stop`, {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify({ operationId: opId }),
      }).catch((err) => {
        console.warn("[useAIQuery] Failed to send query-stop request:", err);
      });
    } catch (err) {
      console.warn("[useAIQuery] Error while sending query-stop request:", err);
    }
  }, [id, setResponses]);

  useEffect(() => {
    return () => {
      if (streamingFlushTimerRef.current) {
        clearTimeout(streamingFlushTimerRef.current);
      }
    };
  }, []);

  const resetQueryLockAfterStop = useCallback(() => {
    setQueryLockedAfterStop(false);
    if (id) {
      localStorage.removeItem(`stopped_chat_${id}`);
    }
  }, [id]);

  return {
    queryLoading,
    setQueryLoading,
    handleSubmitQuery,
    queryLockedAfterStop,
    stopInitialQuery,
    resetQueryLockAfterStop,
  };
}
